import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Button, Text } from "components";
import WizardProgress from "./WizardProgress";
import TitleField from "../../fields/TitleField";
import ThemeField from "../../fields/ThemeField";
import DifficultyField from "../../fields/DifficultyField";
import LengthField from "../../fields/LengthField";
import { AI_LENGTH } from "../../fields/options";
import { Sparkle } from "../../icons";

const STEPS = ["Basics", "Difficulty", "Length"];

const CreateWizard = ({ form, setForm, onSubmit, onBack, submitting = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const last = STEPS.length - 1;
  // Step lives in the URL (?step=N) so each step is a history entry and the
  // browser Back button walks back through the wizard.
  const raw = parseInt(searchParams.get("step") || "0", 10);
  const step = Number.isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), last);
  // Furthest step reached — gates which rail circles are clickable. Kept >= step
  // however step changes (Continue, rail, browser nav).
  const [furthest, setFurthest] = useState(step);
  useEffect(() => { setFurthest((f) => Math.max(f, step)); }, [step]);

  const set = (patch) => setForm({ ...form, ...patch });
  // Per-step "can advance" gate — explicit per step so adding validation later is safe.
  const canNext = [
    form.title.trim().length > 0, // Basics: title required
    true,                         // Difficulty: always defaulted
    true,                         // Length: always defaulted
  ][step];
  // Title is the only hard requirement; guard the final submit so a rail jump
  // can't bypass it.
  const canSubmit = form.title.trim().length > 0;

  const goToStep = (i) => setSearchParams({ step: String(i) }); // push a history entry
  const goTo = (i) => { if (i >= 0 && i <= furthest) goToStep(i); };
  const next = () => {
    if (submitting) return;
    if (step < last) goToStep(step + 1);
    else onSubmit();
  };
  // In-card Back mirrors the browser Back button: pop history a step, or leave
  // the wizard entirely from step 0.
  const back = () => { if (step > 0) navigate(-1); else onBack(); };

  return (
    <div className="cg-stage narrow">
      <WizardProgress steps={STEPS} current={step} maxStep={furthest} onStepClick={goTo} />
      <Card padding="lg">
        <Text variant="eyebrow" tone="accent" className="cg-step-eyebrow">Step {step + 1} of {STEPS.length}</Text>

        {step === 0 && (
          <>
            <Text variant="h2" className="cg-step-title">Name your game</Text>
            <Text variant="body" tone="muted" className="cg-step-sub">Add a theme to steer the questions — or leave it blank and surprise yourself.</Text>
            <div className="cg-step-body">
              <TitleField value={form.title} onChange={(v) => set({ title: v })} />
              <ThemeField value={form.theme} onChange={(v) => set({ theme: v })} />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <Text variant="h2" className="cg-step-title">Set the challenge</Text>
            <Text variant="body" tone="muted" className="cg-step-sub">How tough should the clues be?</Text>
            <div className="cg-step-body">
              <DifficultyField
                value={form.difficulty}
                otherText={form.difficultyOther}
                onChange={(v) => set({ difficulty: v })}
                onOtherText={(v) => set({ difficultyOther: v })}
              />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <Text variant="h2" className="cg-step-title">How long should it run?</Text>
            <Text variant="body" tone="muted" className="cg-step-sub">Pick a length that fits your group.</Text>
            <div className="cg-step-body">
              <LengthField options={AI_LENGTH} value={form.length} onChange={(v) => set({ length: v })} />
            </div>
          </>
        )}

        <div className="cg-wiz-foot">
          <button type="button" className="cg-link-btn" onClick={back} disabled={submitting}>
            ← {step === 0 ? "Back to options" : "Back"}
          </button>
          {step === last
            ? <Button variant="cta" startIcon={submitting ? undefined : <Sparkle size={16} />} loading={submitting} disabled={!canSubmit || submitting} onClick={next}>{submitting ? "Generating…" : "Generate game"}</Button>
            : <Button variant="primary" disabled={!canNext} onClick={next}>Continue →</Button>}
        </div>
      </Card>
    </div>
  );
};

export default CreateWizard;
